import React, {useState} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiCompass, FiTarget, FiTrendingUp} = FiIcons;

const SegmentationGate = () => {
  const [selectedPath, setSelectedPath] = useState(null);

  const paths = [
    {
      id: 'explorer',
      title: 'THE EXPLORER',
      subtitle: 'ผู้แสวงหาตัวตน',
      icon: FiCompass,
      color: 'from-cyan-500 to-blue-500',
      path: 'เส้นทางแห่งการค้นพบตัวตน',
      description: 'สำหรับผู้ที่ต้องการเข้าใจ "พิมพ์เขียวปาจื้อ" ของตัวเองอย่างลึกซึ้ง เพื่อปลดล็อกศักยภาพและสร้างสรรค์เส้นทางที่ไม่เหมือนใคร',
      buttonText: 'เริ่มต้นการค้นพบของคุณ',
      targetSection: 'explorer'
    },
    {
      id: 'strategist',
      title: 'THE STRATEGIST',
      subtitle: 'ผู้สร้างกลยุทธ์',
      icon: FiTarget,
      color: 'from-purple-500 to-indigo-500',
      path: 'เส้นทางแห่งความสำเร็จเชิงกลยุทธ์',
      description: 'สำหรับผู้ที่เชื่อมั่นในความสามารถของตน แต่กำลังมองหา "ความได้เปรียบ" ที่จะช่วยให้การตัดสินใจเฉียบคมและไปถึงเป้าหมายได้เร็วยิ่งขึ้น',
      buttonText: 'ยกระดับกลยุทธ์ของคุณ',
      targetSection: 'strategist'
    },
    {
      id: 'master',
      title: 'THE MASTER',
      subtitle: 'ผู้นำทางชีวิต',
      icon: FiTrendingUp,
      color: 'from-indigo-500 to-purple-500',
      path: 'เส้นทางแห่งความมั่นคงและมั่นใจ',
      description: 'สำหรับผู้ที่ต้องการ "เข็มทิศ" ที่แม่นยำ เพื่อนำทางชีวิตในช่วงต่อไปด้วยความมั่นใจและรอบคอบกว่าที่เคยเป็น',
      buttonText: 'สู่เส้นทางแห่งความมั่นใจ',
      targetSection: 'master'
    }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section className="py-12 sm:py-16 lg:py-20 relative" id="segmentation">
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-blue-900/20 to-slate-900"></div>
      
      <div className="relative z-10 w-full px-3 sm:px-4 lg:px-6 xl:px-8">
        <motion.div
          className="text-center mb-12 sm:mb-16"
          initial={{opacity: 0, y: 30}}
          whileInView={{opacity: 1, y: 0}}
          transition={{duration: 0.8}}
          viewport={{once: true}}
        >
          <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-light text-white mb-6 sm:mb-8 leading-tight px-2">
            บททดสอบแรก: คุณกำลังแสวงหาสิ่งใดบนเส้นทางของคุณ?
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-blue-200 font-light max-w-3xl mx-auto px-4">
            จงฟังเสียงภายใน... แล้วเลือกเส้นทางที่สะท้อนการเดินทางของคุณในตอนนี้
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 max-w-7xl mx-auto">
          {paths.map((path, index) => (
            <motion.div
              key={path.id}
              className={`bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-lg rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-white/20 cursor-pointer transition-all duration-500 ${
                selectedPath === path.id ? 'ring-2 ring-blue-400 scale-105' : 'hover:scale-102'
              }`}
              initial={{opacity: 0, y: 40}}
              whileInView={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: index * 0.2}}
              viewport={{once: true}}
              onClick={() => setSelectedPath(path.id)}
              whileHover={{y: -10}}
            >
              <div className="text-center mb-6 sm:mb-8">
                <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-xl sm:rounded-2xl bg-gradient-to-r ${path.color} flex items-center justify-center mx-auto mb-4 sm:mb-6`}>
                  <SafeIcon icon={path.icon} className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                </div>
                <h3 className="text-xl sm:text-2xl font-light text-white mb-2">{path.title}</h3>
                <p className="text-blue-300 text-xs sm:text-sm font-light mb-3 sm:mb-4">{path.subtitle}</p>
                <div className="h-px bg-gradient-to-r from-transparent via-blue-400 to-transparent mb-4 sm:mb-6"></div>
                <h4 className="text-sm sm:text-base lg:text-lg text-blue-200 font-light mb-4 sm:mb-6 leading-relaxed">
                  {path.path}
                </h4>
              </div>

              <p className="text-slate-300 leading-relaxed font-light text-xs sm:text-sm mb-6 sm:mb-8">
                {path.description}
              </p>

              <motion.button
                className={`w-full bg-gradient-to-r ${path.color} text-white py-3 sm:py-4 rounded-xl sm:rounded-2xl font-light text-sm sm:text-base lg:text-lg shadow-xl hover:shadow-2xl transition-all duration-300`}
                whileHover={{scale: 1.02}}
                whileTap={{scale: 0.98}}
                onClick={(e) => {
                  e.stopPropagation();
                  scrollToSection(path.targetSection);
                }}
              >
                {path.buttonText}
              </motion.button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SegmentationGate;